import PropTypes from "prop-types";
import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { EDITOR_SLIDER_LIMITS } from "../../../../config/newsletterEditorConfig";
import { DEFAULT_BORDER, normalizeBorder } from "../model/borderUtils";
import ColorPickerField from "./ColorPickerField";

const { borderWidthPx, borderRadiusPx } = EDITOR_SLIDER_LIMITS;

const SideBorderEditor = ({ label, side, onChange }) => (
  <Stack spacing={1} sx={{ mb: 2, p: 1, border: "1px solid #e2e8f0" }}>
    <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b" }}>
      {label}
    </Typography>
    <Typography variant="caption">Spessore: {side.width}px</Typography>
    <Slider
      size="small"
      min={borderWidthPx.min}
      max={borderWidthPx.max}
      step={1}
      value={side.width}
      onChange={(_, val) =>
        onChange({ ...side, width: val, style: val > 0 && side.style === "none" ? "solid" : side.style })
      }
    />
    <TextField select fullWidth size="small" label="Stile" value={side.style} onChange={(e) => onChange({ ...side, style: e.target.value })}>
      <MenuItem value="none">Nessuno</MenuItem>
      <MenuItem value="solid">Solido</MenuItem>
      <MenuItem value="dashed">Tratteggiato</MenuItem>
      <MenuItem value="dotted">Punteggiato</MenuItem>
    </TextField>
    <ColorPickerField label="Colore" value={side.color} onChange={(color) => onChange({ ...side, color })} />
    <Typography variant="caption">Raggio: {side.radius}px</Typography>
    <Slider size="small" min={borderRadiusPx.min} max={borderRadiusPx.max} step={1} value={side.radius} onChange={(_, val) => onChange({ ...side, radius: val })} />
  </Stack>
);

SideBorderEditor.propTypes = {
  label: PropTypes.string.isRequired,
  side: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

const BorderEditor = ({ label = "Bordo", value, onChange }) => {
  const border = normalizeBorder(value);

  const update = (patch) => onChange({ ...border, ...patch });

  const updateSide = (sideKey, sideValue) =>
    onChange({
      ...border,
      advanced: true,
      [sideKey]: sideValue,
    });

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
        {label}
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={Boolean(border.advanced)}
            onChange={(e) => update({ advanced: e.target.checked })}
          />
        }
        label={<Typography variant="body2">Bordo avanzato</Typography>}
      />

      {!border.advanced ? (
        <>
          <Typography variant="caption">Spessore: {border.width}px</Typography>
          <Slider
            size="small"
            min={borderWidthPx.min}
            max={borderWidthPx.max}
            step={1}
            value={border.width}
            onChange={(_, val) =>
              update({ width: val, style: val > 0 && border.style === "none" ? "solid" : border.style })
            }
          />
          <TextField select fullWidth size="small" label="Stile" value={border.style} onChange={(e) => update({ style: e.target.value })}>
            <MenuItem value="none">Nessuno</MenuItem>
            <MenuItem value="solid">Solido</MenuItem>
            <MenuItem value="dashed">Tratteggiato</MenuItem>
            <MenuItem value="dotted">Punteggiato</MenuItem>
          </TextField>
          <ColorPickerField
            label="Colore bordo"
            value={border.color || DEFAULT_BORDER.color}
            onChange={(color) => update({ color })}
          />
          <Typography variant="caption">Raggio angoli: {border.radius}px</Typography>
          <Slider size="small" min={borderRadiusPx.min} max={borderRadiusPx.max} step={1} value={border.radius} onChange={(_, val) => update({ radius: val })} />
        </>
      ) : (
        <>
          <SideBorderEditor label="Top" side={border.top} onChange={(side) => updateSide("top", side)} />
          <SideBorderEditor label="Right" side={border.right} onChange={(side) => updateSide("right", side)} />
          <SideBorderEditor label="Bottom" side={border.bottom} onChange={(side) => updateSide("bottom", side)} />
          <SideBorderEditor label="Left" side={border.left} onChange={(side) => updateSide("left", side)} />
        </>
      )}
    </Stack>
  );
};

BorderEditor.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
};

export default BorderEditor;
 